import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiDelete, operationsClient } from '@/lib/api';
import { jobEndpoints } from '@/lib/api/endpoints';
import { contactKeys, jobKeys, noteKeys } from '@/lib/queryKeys';
import type { MediaItem } from '@/types/media';

/**
 * Deletes the original attachment behind a roll-up tile. The server validates
 * jobs:edit scope and attachment→note→job ownership, then records the audit
 * line. Both upward views are invalidated because the object is referenced,
 * never copied.
 */
export function useDeleteJobMedia(contactId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (item: MediaItem) => {
      if (!item.origin.jobId) throw new Error('Media has no owning job');
      return apiDelete<{ deleted: true }>(
        operationsClient,
        jobEndpoints.deleteMedia(item.origin.jobId, item.id),
      );
    },
    onSuccess: async (_result, item) => {
      const ownerNoteKey =
        item.origin.level === 'visit' && item.origin.appointmentId
          ? noteKeys.entity('APPOINTMENT', item.origin.appointmentId)
          : noteKeys.entity('JOB', item.origin.jobId);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: jobKeys.mediaBase(item.origin.jobId),
        }),
        queryClient.invalidateQueries({
          queryKey: jobKeys.activity(item.origin.jobId),
        }),
        queryClient.invalidateQueries({ queryKey: ownerNoteKey }),
        ...(contactId
          ? [
              queryClient.invalidateQueries({
                queryKey: contactKeys.mediaBase(contactId),
              }),
            ]
          : []),
      ]);
      toast.success('Media deleted.');
    },
    onError: () => toast.error("Couldn't delete media."),
  });
}
